//initial redux
import moment from "moment-timezone";
import { all, put, call, fork, select, take, takeEvery } from "redux-saga/effects";
import pointsservice from "../../../../services/points";
import reportsservice from "../../../../services/reports";
import { types as sitetreetypes } from "../../../../services/SiteTree/redux";
import { DateTime, number } from "../../../../utils";
import pointName from "../../../../utils/pointName";
import seriesColor from "../../../../utils/seriesColor";
import steppedSeries from "../../../../utils/steppedSeries";
import unitsservice from "../../../../utils/units";
import { actions as charttooltipactions } from "../ChartTooltip/redux";
import { actions as seriesColorActions } from "../../../../services/SeriesColor/redux";

const initialState = {};

/////////
//types
export const types = {
	LOAD_DATA: "components/validationrulechart/LOAD_DATA"
};

///////////
//reducers
export const reducers = {
	reducer(state = initialState, action) {
		switch (action.type) {
			case types.LOAD_DATA: {
				return state;
			}
			default:
				return state;
		}
	}
};

function getColorForRunState(runState) {
	switch (runState) {
		case 0: //pre con fail
			return null;
		case 1: //test start
			return null;
		case 2: //rule pass
			return "#C7F6CC";
		case 3: //rule fail
			return "#FCD1D0";
		case 4: //rule pause
			return "#FDEABF";
		case 5: //test end
			return null;
		case 6: //rule abort
			return null;
		default:
			return null;
	}
}

//TODO => toDate needs to be replaced with the last processed date on the rule engine for the rule instance.
function addOutcomes(chart, outcomePeriods, pastValues, toDate, component) {
	if (!chart.options) {
		return;
	}
	if (outcomePeriods && outcomePeriods.length) {
		let pointPastSeries = {};

		for (var ii = 0; ii < component.inputs.length; ii++) {
			var wkInput = component.inputs[ii];

			for (var jj = 0; jj < wkInput.values.length; jj++) {
				var wkValue = wkInput.values[jj];

				let axisIndex = getAxisIndex(chart, wkValue.units);
				let pastSeries = {
					type: "column",
					yAxis: axisIndex,
					data: [],
					marker: {
						enabled: false
					},
					enableMouseTracking: false,
					showInLegend: false
				};

				pointPastSeries[wkValue.value] = pastSeries;
				chart.addSeries(pastSeries);
			}
		}

		outcomePeriods.forEach((outcomePeriod, index) => {
			var bandColor = getColorForRunState(outcomePeriod.runState);
			const pastSeries = pointPastSeries[outcomePeriod.pointId];

			if (bandColor != null && outcomePeriod) {
				const start = outcomePeriod.startSiteLocal;
				const end = outcomePeriod.endSiteLocal;

				const columns = pastValues
					.filter(x => x.pointId === wkValue.value && x.dateSiteLocal >= start && x.dateSiteLocal <= end)
					.map(val => ({
						color: bandColor,
						x: val.dateSiteLocal,
						y: val.value
					}));

				pastSeries.data.push(columns);
			}
		});
	}
}

function configYAxis(units, min, max, visible) {
	let newYAxis = {
		min,
		max,
		visible,
		title: {
			text: units,
			align: "high",
			rotation: 0,
			margin: 0,
			y: -30,
			offset: 14,
			style: {
				"font-weight": "600",
				"font-size": "15px"
			}
		},
		labels: {
			style: {
				fontSize: "14px",
				color: "#999"
			},
			formatter() {
				return number.parseNumber(this.value, 1).replace(/0*$|\.0*$|\.$/g, "");
			}
		}
	};

	if (units == "inSession" ) {
		newYAxis.min = 0;
		newYAxis.max = 1;
		newYAxis.height = "20%";
		newYAxis.top = "80%";
	}

	return newYAxis;
}

//returns teh index of the y index with teh specified units.
//if it doesn't exist it creates a new axis with teh units and returns the index for that axis.
function getAxisIndex(chart, units, min, max, visible = true, forceNew = false) {
	if (!chart.options) {
		return;
	}
	let axisIndex = 0;
	let symbol = unitsservice.stringToSymbol(units);
	units = symbol ? symbol : units;
	//if the first yaxis doesnt have a value set then
	if (!forceNew && !chart.yAxis[0].userOptions.title) {
		chart.yAxis[0].update(configYAxis(units, min, max, visible));
	} else {
		//search for existing with same units and use that, else add a new axis.
		let axisFound = false;
		for (var ii = 0; ii < chart.yAxis.length; ii++) {
			let wkAxis = chart.yAxis[ii];
			if (wkAxis.userOptions.title && wkAxis.userOptions.title.text == units) {
				axisFound = true;
				axisIndex = ii;
				break;
			}
		}
		if (!axisFound) {
			axisIndex = chart.yAxis.length;
			chart.addAxis(configYAxis(units, min, max, visible));
		}
	}
	return axisIndex;
}

function* addPointData(wkInput, chart, pointDetails, pointData, coV, fromDate, toDate, now, id) {
	if (!chart.options) {
		return;
	}

	const colors = seriesColor.getSeriesColors();
	let { colorDictionary, colorIndex } = yield select(x => x.seriesColor);

	let color = colorDictionary[pointDetails.name] ? colorDictionary[pointDetails.name] : null;
	if (!color) {
		var newColor = seriesColor.getColorForPoint(pointDetails.name);
		if (newColor) {
			color = newColor;
		} else {
			color = colors[colorIndex];
			colorDictionary[pointDetails.name] = color;
			colorIndex++;
			if (colorIndex >= colors.length) {
				colorIndex = 0;
			}
			yield put(seriesColorActions.newPoint(pointDetails.name, color));
		}
	}

	let newSeries = {
		name: pointName.formatDescription(pointDetails),
		color: color,
		yAxis: getAxisIndex(chart, pointDetails.units),
		data: [],
		marker: {
			enabled: false
		}
	};
	let isSteppedSeries = steppedSeries.isSteppedSeries(pointDetails.name, pointDetails.description, coV);

	let previousValue = null;
	if (coV && wkInput.source === "IEnergy") {
		previousValue = yield call(pointsservice.TrendableDataGetAsAt, pointDetails.objectID, fromDate);
		if (previousValue instanceof Error || !previousValue) {
			return;
		}
		if (previousValue && previousValue.length) {
			let wkPreviousValue = previousValue[0];
			if (wkPreviousValue) {
				newSeries.data.unshift({
					x: DateTime.getGMTDate(fromDate.format("YYYY-MM-DD HH:mm:ss")).getTime(),
					y: wkPreviousValue.value,
					units: pointDetails.units
				});
			}
		}
	}

	if (pointData && pointData.length) {
		pointData.forEach(s => {
			newSeries.data.push({
				x: DateTime.getGMTDate(s.timestamp).getTime(),
				y: s.value,
				units: pointDetails.units
			});
		});
	}

	if (isSteppedSeries) {
		newSeries.step = "left";
	}

	if (coV) {
		//only add a cov up to present time
		const endDate = now.valueOf() < toDate.valueOf() ? now : toDate;
		if (pointData && pointData.length) {
			newSeries.data.push({
				x: DateTime.getGMTDate(endDate.format("YYYY-MM-DD HH:mm:ss")).getTime(),
				y: pointData[pointData.length - 1].value,
				units: pointDetails.units
			});
		} else if (previousValue && previousValue.length) {
			let wkPreviousValue = previousValue[0];
			if (wkPreviousValue) {
				newSeries.data.push({
					x: DateTime.getGMTDate(endDate.format("YYYY-MM-DD HH:mm:ss")).getTime(),
					y: wkPreviousValue.value,
					units: pointDetails.units
				});
			}
		}
	}
	if (chart && chart.options) {
		chart.addSeries(newSeries);
		yield put(charttooltipactions.addSeries(id, newSeries));
	}
}

function* addOutcomesAsync(chart, ruleInstanceId, fromDate, toDate, component) {
	const outcomePeriods = yield call(reportsservice.GetRunTimesAndFailures, ruleInstanceId, fromDate, toDate, false);
	if (outcomePeriods instanceof Error || !outcomePeriods) {
		return;
	}
	const pastValues = yield call(reportsservice.GetDerivedPastValues, ruleInstanceId, fromDate, toDate);
	if (pastValues instanceof Error || !pastValues) {
		return;
	}

	addOutcomes(chart, outcomePeriods, pastValues, toDate, component);
}

function* addPointDataAsync(wkInput, chart, pointId, fromDate, toDate, coV, index, now, id) {
	if (wkInput.source === "RuleInstance") {
		const [pointDetails, pointData] = yield all([
			call(pointsservice.GetRuleInstance, pointId),
			call(pointsservice.GetRuleInstanceData, pointId, wkInput.subValues[0].values[index].value, fromDate, toDate)
		]);
		yield call(addPointData, wkInput, chart, pointDetails, pointData, true, fromDate, toDate, now, id);
	} else if (wkInput.source === "IEnergy") {
		const [pointDetails, pointData] = yield all([
			call(pointsservice.TrendableObjectGetObject, pointId),
			call(pointsservice.TrendableDataGetData, pointId, fromDate, toDate)
		]);
		yield call(addPointData, wkInput, chart, pointDetails, pointData, coV, fromDate, toDate, now, id);
	}
}

function* addUpperLimit(chart, ruleInstanceId, fromDate, toDate, now, id) {
	//only add a cov up to present time
	const endDate = now.valueOf() < toDate.valueOf() ? now : toDate;
	const result = yield call(reportsservice.GetUpperLimit, ruleInstanceId, fromDate, endDate);
	if (result instanceof Error || !result) {
		return;
	}
	var yAxisIndex = 0;
	if (result.processVariablePointId) {
		const pointDetails = yield call(pointsservice.TrendableObjectGetObject, result.processVariablePointId);
		if (pointDetails instanceof Error || !pointDetails) {
			return;
		}
		if (pointDetails && pointDetails.units) {
			yAxisIndex = getAxisIndex(chart, pointDetails.units);
		}
	}
	let seriesName = "Upper Limit";
	let newSeries = {
		name: seriesName,
		step: "left",
		yAxis: yAxisIndex,
		color: seriesColor.getColorForPoint(seriesName),
		data: [],
		marker: {
			enabled: false
		}
	};
	if (result && result.data && result.data.length) {
		result.data.forEach(s => {
			newSeries.data.push([new Date(s.timestamp).getTime(), s.value]);
		});
	}
	if (chart && chart.options) {
		chart.addSeries(newSeries);
		yield put(charttooltipactions.addSeries(id, newSeries));
	}
}

function* addLowerLimit(chart, ruleInstanceId, fromDate, toDate, now, id) {
	//only add a cov up to present time
	const endDate = now.valueOf() < toDate.valueOf() ? now : toDate;
	const result = yield call(reportsservice.GetLowerLimit, ruleInstanceId, fromDate, endDate);
	if (result instanceof Error || !result) {
		return;
	}
	var yAxisIndex = 0;
	if (result.processVariablePointId) {
		const pointDetails = yield call(pointsservice.TrendableObjectGetObject, result.processVariablePointId);
		if (pointDetails instanceof Error || !pointDetails) {
			return;
		}
		if (pointDetails && pointDetails.units) {
			yAxisIndex = getAxisIndex(chart, pointDetails.units);
		}
	}
	let seriesName = "Lower Limit";
	let newSeries = {
		name: seriesName,
		step: "left",
		yAxis: yAxisIndex,
		color: seriesColor.getColorForPoint(seriesName),
		data: [],
		marker: {
			enabled: false
		}
	};
	if (result && result.data && result.data.length) {
		result.data.forEach(s => {
			newSeries.data.push([new Date(s.timestamp).getTime(), s.value]);
		});
	}
	if (chart && chart.options) {
		chart.addSeries(newSeries);
		yield put(charttooltipactions.addSeries(id, newSeries));
	}
}

//////////
//sagas
export const sagas = {
	*rootSaga() {
		yield all([this.loadData()]);
	},

	*loadData() {
		yield takeEvery(types.LOAD_DATA, function*({ payload }) {
			const component = payload.component;
			const chart = payload.chart;
			const id = payload.id;
			const fromDate = moment(payload.timePeriod.startDate);
			const toDate = moment(payload.timePeriod.endDate);
			let currentSite = yield select(x => x.sitetree.currentSite);
			if (!currentSite) {
				yield take(sitetreetypes.ON_LOAD);
				currentSite = yield select(x => x.sitetree.currentSite);
			}
			let utcOffset = 0;
			if (currentSite.momentTimezone) {
				utcOffset = moment()
					.tz(currentSite.momentTimezone)
					.utcOffset();
			}
			let now = moment(
				moment()
					.utc()
					.add(utcOffset, "m")
					.format("YYYY-MM-DD HH:mm:ss"),
				"YYYY-MM-DD HH:mm:ss"
			);

			if (chart.options) {
				chart.xAxis[0].setExtremes(
					moment(fromDate)
						.utc()
						.add(fromDate.utcOffset(), "m")
						.valueOf(),
					moment(toDate)
						.utc()
						.add(fromDate.utcOffset(), "m")
						.valueOf()
				);
			}

			//add all calls into aray so can do a yield all on them
			let dataCalls = [];

			for (var ii = 0; ii < component.inputs.length; ii++) {
				var wkInput = component.inputs[ii];

				for (var jj = 0; jj < wkInput.values.length; jj++) {
					var wkValue = wkInput.values[jj];
					dataCalls.push({
						func: addPointDataAsync,
						args: [wkInput, chart, wkValue.value, fromDate, toDate, wkValue.coV, jj, now, id]
					});
				}
			}
			dataCalls.push({
				func: addOutcomesAsync,
				args: [chart, component.ruleInstanceId, fromDate, toDate, component]
			});
			let calls = [];
			dataCalls.forEach(s => calls.push(call(s.func, ...s.args)));
			yield all(calls);
			if (payload.onLoadComplete) {
				payload.onLoadComplete();
			}
		});
	}
};

////////
//actions
export const actions = {
	loadData(component, chart, timePeriod, onLoadComplete, id) {
		return {
			type: types.LOAD_DATA,
			payload: { component, chart, timePeriod, onLoadComplete, id }
		};
	}
};



// WEBPACK FOOTER //
// ./src/components/ReportComponents/Charts/ValidationWithBarChart/redux.js